/**
 * WP8 · BK-Profil — Form-Sections
 *
 * Aufbau nach dem PB-Muster (`pflege/profile/sections.tsx`), aber mit dem
 * BK-Feldsatz aus CRM 1.0 (user_role === 1):
 *
 *   01 Stammdaten          — Person, Kontakt (inkl. mobile), Anschrift
 *   02 Versicherung & Qual.— Krankenkasse, Kennnummern (sozial_vnr, lbnr,
 *                            steuer_id), Qualifikation (4 Optionen)
 *   03 Erfahrung & Sprache — experience, german_skills (0–10, 0.5), bk_deutsch
 *   04 Präferenzen/Einsatz — bk_wohnen, bk_aktivitaeten, bk_anrede,
 *                            bk_fuehrerschein
 *   05 Freitexte           — PL zuerst (read-only Übersetzung), dann DE
 *   10 Status              — badge, impfstatus, status, pause_von/bis (Konto-Status seit 23.10.2026 nicht mehr im Formular)
 *   11 Bank                — kontoinhaber, bankname, iban
 *   12 Sonstiges           — description
 *
 * KEINE Phasen-/Pflege-Elemente (PhaseBar, Pflege-Parameter, SIS, Wunsch-BK).
 * Controls werden 1:1 aus `pflege/profile/controls` wiederverwendet;
 * i18n-Labels aus `pflege.form.*` wo identisch, sonst `betreuung.form.*`.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../../lib/theme';
import { Button } from '../../../components/ui';
import {
  Section, GroupLabel, Grid, Field, Text, Area, Select, CheckGroup,
  Range, Rating, ToggleRow, ActionStatus, HistoryTable, asMonth, asEuro,
} from '../../pflege/profile/controls';
import { HARDCODED, BK_HARDCODED, fieldOptions, bkStatusOptions } from './options';
import { CareParamIcon } from '../../../components/care/CareParamIcons';
import { DateInput } from '../../../components/DateInput';
import { v2PriceBonus, priceBonusErrorMessage } from '../../../lib/priceBonus';
import {
  v1Users, v1Contacts,
  type V1ProfileOption, type V1User, type V1Contact, type V1ContactType,
} from '../../../lib/v1';
import { useOptionLabel } from '../../../lib/useOptionLabel';

/** Flacher Formular-State des BK-Profils (User- + Profil-Felder gemischt). */
export interface BkFormState {
  // users (Lifecycle/Status)
  status?: string; konto?: string;
  badge?: string; impfstatus?: string;
  rating?: number;
  pause_von?: string; pause_bis?: string;
  // users (Erfahrung & Sprache)
  experience?: string; german_skills?: string;
  // users (Kontakt & Anschrift)
  email?: string; mobile?: string; telefon?: string;
  strasse?: string; hausnummer?: string; plz?: string; ort?: string; land?: string;
  // users (Bank & Sonstiges)
  kontoinhaber?: string; bankname?: string; iban?: string;
  description?: string;
  // profiles (Stammdaten)
  anrede?: string; titel?: string; vorname?: string; nachname?: string;
  geburtsdatum?: string; nationalitaet?: string;
  groesse?: string; gewicht?: string; familienstand?: string; raucher?: string;
  // profiles (Versicherung & Qualifikation)
  krankenkasse?: string; krankenkasse_typ?: string;
  kranken_vnr?: string; sozial_vnr?: string; steuer_id?: string; lbnr?: string;
  qualifikation?: string;
  // profiles (Präferenzen/Einsatz + Sprache)
  bk_wohnen?: string[]; bk_aktivitaeten?: string[]; bk_anrede?: string[];
  bk_deutsch?: string; bk_fuehrerschein?: string;
  // BK akzeptiert ein ausgeprägtes Familienleben (geteilte Spalte mit dem PB-Profil)
  ausgepraegtes_familienleben?: boolean;
  // BK akzeptiert einen Raucherhaushalt (Matching gegen das Familienkonto)
  raucherhaushalt_akzeptiert?: boolean;
  // profiles (Freitexte, DE)
  hobbys?: string; umfeld?: string; sonstiges?: string;
  // profiles (Einsatz-Parameter — geteilter Katalog mit dem PB-Profil)
  // Mehrfachauswahl: welche Wohnsituationen/-orte die BK akzeptiert.
  wohnsituation?: string[]; wohnort?: string[];
  haustiere?: string; haustiere_welche?: string[];
  charaktereigenschaften?: string[];
  // profiles (Parameter — Pflege-Katalog, den die BK akzeptiert; wie CRM 1.0)
  // Batch-12: beweglichkeit ist bei BKs eine Mehrfachauswahl.
  pflegegrad?: string; beweglichkeit?: string[];
  // Batch-12: BK übernimmt SGB-V-Leistungen (Checkbox in 06 Einsatzparameter).
  sgb5_bk?: boolean;
  einschraenkungen?: string[]; sinneswahrnehmung?: string[];
  geisteszustand?: string; schlafverhalten?: string;
  grundpflege?: string; hygiene?: string; ankleiden?: string;
  essen?: string; urinkontrolle?: string; stuhlkontrolle?: string;
  wohnzustand?: string[]; einkaufsmoeglichkeiten?: string;
}

/** Read-only PL-Übersetzungen der Freitexte (aus profile_field_translations). */
export interface BkPlTexts {
  hobbys?: string;
  umfeld?: string;
  sonstiges?: string;
}

// Navigations-Struktur — Labels zur Laufzeit über t(). Wo das Label mit dem
// PB-Formular identisch ist, wird der pflege.form-Key wiederverwendet.
export const BK_SECTIONS_NAV = [
  { groupKey: 'pflege.form.group.profile', items: [
    { id: 'stammdaten',   num: '01', labelKey: 'pflege.form.section.stammdaten.nav' },
    { id: 'versicherung', num: '02', labelKey: 'betreuung.form.section.versicherung.nav' },
    { id: 'erfahrung',    num: '03', labelKey: 'betreuung.form.section.erfahrung.nav' },
    { id: 'praeferenzen', num: '04', labelKey: 'betreuung.form.section.praeferenzen.nav' },
    { id: 'freitexte',    num: '05', labelKey: 'pflege.form.section.freitexte.nav' },
    { id: 'parameter',    num: '06', labelKey: 'betreuung.form.section.parameter.nav' },
  ]},
  { groupKey: 'pflege.form.group.admin', items: [
    { id: 'status',    num: '10', labelKey: 'pflege.form.section.status.nav' },
    { id: 'bank',      num: '11', labelKey: 'pflege.form.section.bank.nav' },
    { id: 'sonstiges', num: '12', labelKey: 'pflege.form.section.sonstiges.nav' },
    { id: 'vertrag',        num: '13', labelKey: 'betreuung.form.section.vertrag.nav' },
    { id: 'bonus',          num: '14', labelKey: 'betreuung.form.section.bonus.nav' },
    { id: 'ansprechpartner', num: '15', labelKey: 'betreuung.form.section.ansprechpartner.nav' },
  ]},
] as const;

interface Props {
  f: BkFormState;
  set: <K extends keyof BkFormState>(key: K, val: BkFormState[K]) => void;
  opts: Record<string, V1ProfileOption[]> | null;
  dark: boolean;
  /** Felder mit Validierungsfehler — bekommen rote Umrandung. */
  invalidFields?: Array<keyof BkFormState>;
  /** PL-Übersetzungen der Freitexte (read-only, PL-zuerst-Reihenfolge). */
  plTexts?: BkPlTexts;
  /**
   * Voller V1User — für die Aktions-Sections (Vertrag/Bonus/Ansprechpartner),
   * die eigene Endpunkte nutzen und Historien aus user.prices/user.boni lesen.
   */
  user?: V1User | null;
}

/** Read-only Mehrzeilen-Anzeige für die PL-Freitexte (theme-aware). */
function PlText({ value, dark }: { value: string | undefined; dark: boolean }) {
  const t = getThemeColors(dark);
  const empty = !value || !value.trim();
  const { t: tr } = useTranslation();
  return (
    <div style={{
      border: `1px solid ${t.borderSoft}`, borderRadius: 8,
      background: t.cardMuted, padding: '9px 12px',
      fontSize: 13, color: empty ? t.muted : t.text,
      lineHeight: 1.5, whiteSpace: 'pre-wrap', minHeight: 20,
      fontFamily: 'Outfit,sans-serif',
    }}>
      {empty ? tr('betreuung.form.pl_empty') : value}
    </div>
  );
}

/** Datumsfeld „TT.MM.JJJJ" im Look der Profil-Inputs (Wert bleibt YYYY-MM-DD). */
function DateField({ value, onChange, ariaLabel, dark }: {
  value: string | undefined; onChange: (v: string) => void; ariaLabel: string; dark: boolean;
}) {
  const t = getThemeColors(dark);
  return (
    <DateInput
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      style={{
        fontFamily: 'Outfit, sans-serif', fontSize: 14, lineHeight: '20px',
        border: `1px solid ${t.border}`, borderRadius: 8, padding: '9px 12px',
        color: t.heading, background: t.inputBg, width: '100%',
      }}
    />
  );
}

export function BkFormSections({ f, set, opts, dark, invalidFields, plTexts, user }: Props) {
  const { t: tr } = useTranslation();

  const isInvalid = (key: keyof BkFormState) => (invalidFields ?? []).includes(key);

  // shorthand-Bindings analog PB-FormSections
  const b = <K extends keyof BkFormState>(key: K) => ({
    value: f[key] as string | undefined,
    onChange: (v: string) => set(key, v as BkFormState[K]),
  });
  const bMulti = <K extends keyof BkFormState>(key: K) => ({
    value: (f[key] as string[] | undefined) ?? [],
    onChange: (v: string[]) => set(key, v as BkFormState[K]),
  });
  const bBool = <K extends keyof BkFormState>(key: K) => ({
    value: f[key] as boolean | undefined,
    onChange: (v: boolean) => set(key, v as BkFormState[K]),
  });

  const opt = (k: string) => fieldOptions(k, opts);
  /** Geteiltes Label aus dem PB-Formular (identischer Text). */
  const fl = (k: string) => tr(`pflege.form.field.${k}`);
  /** BK-spezifisches Label (eigene Perspektive/nur BK-Feld). */
  const bl = (k: string) => tr(`betreuung.form.field.${k}`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* 01 · Stammdaten (inkl. Kontakt mit mobile & Anschrift) */}
      <Section dark={dark} id="stammdaten" num="01" title={tr('pflege.form.section.stammdaten.title')} desc={tr('pflege.form.section.stammdaten.desc')}>
        <Grid cols={4}>
          <Field dark={dark} required label={fl('anrede')} hasError={isInvalid('anrede')}><Select dark={dark} {...b('anrede')} placeholder="—" options={HARDCODED.anrede} hasError={isInvalid('anrede')} /></Field>
          <Field dark={dark} label={fl('titel')}><Text dark={dark} {...b('titel')} placeholder={tr('pflege.form.ph.titel')} /></Field>
          <Field dark={dark} required label={fl('vorname')} hasError={isInvalid('vorname')}><Text dark={dark} {...b('vorname')} placeholder={tr('pflege.form.ph.vorname')} hasError={isInvalid('vorname')} /></Field>
          <Field dark={dark} required label={fl('nachname')} hasError={isInvalid('nachname')}><Text dark={dark} {...b('nachname')} placeholder={tr('pflege.form.ph.nachname')} hasError={isInvalid('nachname')} /></Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('geburtsdatum')}><DateField dark={dark} {...b('geburtsdatum')} ariaLabel={fl('geburtsdatum')} /></Field>
            <Field dark={dark} label={fl('nationalitaet')}><Text dark={dark} {...b('nationalitaet')} placeholder={tr('pflege.form.ph.nationalitaet')} /></Field>
          </Grid>
        </div>
        <div style={{ marginTop: 16 }}>
          <Grid cols={4}>
            <Field dark={dark} label={fl('groesse')}><Text dark={dark} {...b('groesse')} type="number" suffix="cm" placeholder="170" /></Field>
            <Field dark={dark} label={fl('gewicht')}><Text dark={dark} {...b('gewicht')} type="number" suffix="kg" placeholder="65" /></Field>
            <Field dark={dark} label={fl('familienstand')}><Select dark={dark} {...b('familienstand')} placeholder="—" options={opt('familienstand')} /></Field>
            <Field dark={dark} label={fl('raucher')}><Select dark={dark} {...b('raucher')} placeholder="—" options={opt('raucher')} /></Field>
          </Grid>
        </div>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.kontakt')}</GroupLabel>
        <Grid cols={3}>
          <Field dark={dark} label={fl('email')} hint={tr('pflege.form.hint.unique')}><Text dark={dark} {...b('email')} type="email" placeholder="name@beispiel.de" /></Field>
          <Field dark={dark} label={fl('mobile')} hint={tr('pflege.form.hint.unique')}><Text dark={dark} {...b('mobile')} type="tel" placeholder="+49 170 1234567" /></Field>
          <Field dark={dark} label={fl('telefon')}><Text dark={dark} {...b('telefon')} type="tel" placeholder="+49 89 1234567" /></Field>
        </Grid>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.anschrift')}</GroupLabel>
        <Grid cols={6}>
          <Field dark={dark} label={fl('strasse')} span={4}><Text dark={dark} {...b('strasse')} placeholder={tr('pflege.form.ph.strasse')} /></Field>
          <Field dark={dark} label={fl('hausnummer')} span={2}><Text dark={dark} {...b('hausnummer')} placeholder="12a" /></Field>
          <Field dark={dark} label={fl('plz')} span={2}><Text dark={dark} {...b('plz')} placeholder="80331" /></Field>
          <Field dark={dark} label={fl('ort')} span={2}><Text dark={dark} {...b('ort')} placeholder={tr('pflege.form.ph.ort')} /></Field>
          <Field dark={dark} label={fl('land')} span={2}><Select dark={dark} {...b('land')} placeholder="—" options={HARDCODED.land} /></Field>
        </Grid>
      </Section>

      {/* 02 · Versicherung & Qualifikation */}
      <Section dark={dark} id="versicherung" num="02" title={tr('betreuung.form.section.versicherung.title')} desc={tr('betreuung.form.section.versicherung.desc')}>
        <Grid cols={2}>
          <Field dark={dark} label={fl('krankenkasse')}><Text dark={dark} {...b('krankenkasse')} placeholder={tr('pflege.form.ph.krankenkasse')} /></Field>
          <Field dark={dark} label={fl('krankenkasse_typ')}>
            <Select dark={dark} {...b('krankenkasse_typ')} placeholder="—" options={HARDCODED.krankenkasse_typ} />
          </Field>
        </Grid>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.kennnummern')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('kranken_vnr')}><Text dark={dark} {...b('kranken_vnr')} placeholder="A123456789" /></Field>
          <Field dark={dark} label={fl('sozial_vnr')}><Text dark={dark} {...b('sozial_vnr')} placeholder="65 120366 M 123" /></Field>
          <Field dark={dark} label={fl('steuer_id')}><Text dark={dark} {...b('steuer_id')} placeholder="12 345 678 901" /></Field>
          <Field dark={dark} label={fl('lbnr')}><Text dark={dark} {...b('lbnr')} placeholder="LB-00482" /></Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <Grid cols={1}>
            <Field dark={dark} label={bl('qualifikation')}>
              <Select dark={dark} {...b('qualifikation')} placeholder="—" options={BK_HARDCODED.qualifikation} />
            </Field>
          </Grid>
        </div>
      </Section>

      {/* 03 · Erfahrung & Sprache */}
      <Section dark={dark} id="erfahrung" num="03" title={tr('betreuung.form.section.erfahrung.title')} desc={tr('betreuung.form.section.erfahrung.desc')}>
        <Grid cols={2}>
          <Field dark={dark} label={bl('experience')}><Text dark={dark} {...b('experience')} type="number" placeholder="5" /></Field>
          <Field dark={dark} label={bl('bk_deutsch')}><Select dark={dark} {...b('bk_deutsch')} placeholder="—" options={opt('bk_deutsch')} /></Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <Field dark={dark} label={fl('german_skills')} hint={tr('pflege.form.hint.scale_0_10')}>
            <Range dark={dark} {...b('german_skills')} min={0} max={10} step={0.5} />
          </Field>
        </div>
      </Section>

      {/* 04 · Präferenzen / Einsatz (bk_*-Parameter aus BK-Sicht) */}
      <Section dark={dark} id="praeferenzen" num="04" title={tr('betreuung.form.section.praeferenzen.title')} desc={tr('betreuung.form.section.praeferenzen.desc')}>
        <Field dark={dark} label={bl('bk_wohnen')} hint={tr('pflege.form.hint.multi')}>
          <CheckGroup dark={dark} {...bMulti('bk_wohnen')} cols={2} options={opt('bk_wohnen')} />
        </Field>
        {/* Ausgeprägtes Familienleben — Checkbox (bool-Spalte); bei Auswahl
            erscheint das Familien-Icon (CareParamIcons 'familienleben'). */}
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CheckGroup
              dark={dark}
              cols={1}
              value={f.ausgepraegtes_familienleben ? ['1'] : []}
              onChange={v => set('ausgepraegtes_familienleben', v.includes('1'))}
              options={[{ value: '1', label: fl('ausgepraegtes_familienleben') }]}
            />
          </div>
          {f.ausgepraegtes_familienleben && (
            <span style={{ color: getThemeColors(dark).heading, display: 'inline-flex', flexShrink: 0 }}>
              <CareParamIcon k="familienleben" size={24} />
            </span>
          )}
        </div>
        {/* Raucherhaushalt akzeptiert — Gegenstück zu „Raucherhaushalt" im Familienkonto */}
        <div style={{ marginTop: 8 }}>
          <CheckGroup
            dark={dark}
            cols={1}
            value={f.raucherhaushalt_akzeptiert ? ['1'] : []}
            onChange={v => set('raucherhaushalt_akzeptiert', v.includes('1'))}
            options={[{ value: '1', label: bl('raucherhaushalt_akzeptiert') }]}
          />
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={bl('bk_aktivitaeten')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('bk_aktivitaeten')} cols={2} options={opt('bk_aktivitaeten')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={bl('bk_anrede')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('bk_anrede')} cols={3} options={opt('bk_anrede')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Grid cols={2}>
            <Field dark={dark} label={bl('bk_fuehrerschein')}><Select dark={dark} {...b('bk_fuehrerschein')} placeholder="—" options={opt('bk_fuehrerschein')} /></Field>
          </Grid>
        </div>
      </Section>

      {/* 05 · Freitexte — PL zuerst (CRM 1.0: BK-Tab-Reihenfolge invertiert) */}
      <Section dark={dark} id="freitexte" num="05" title={tr('pflege.form.section.freitexte.title')} desc={tr('betreuung.form.section.freitexte.desc')}>
        <GroupLabel dark={dark} top>{tr('betreuung.form.glabel.freitexte_pl')}</GroupLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field dark={dark} label={fl('hobbys')}><PlText dark={dark} value={plTexts?.hobbys} /></Field>
          <Field dark={dark} label={fl('umfeld')}><PlText dark={dark} value={plTexts?.umfeld} /></Field>
          <Field dark={dark} label={fl('sonstiges')}><PlText dark={dark} value={plTexts?.sonstiges} /></Field>
        </div>
        <GroupLabel dark={dark}>{tr('betreuung.form.glabel.freitexte_de')}</GroupLabel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field dark={dark} label={fl('hobbys')}><Area dark={dark} {...b('hobbys')} rows={2} placeholder={tr('pflege.form.ph.hobbys')} /></Field>
          <Field dark={dark} label={fl('umfeld')}><Area dark={dark} {...b('umfeld')} rows={2} placeholder={tr('pflege.form.ph.umfeld')} /></Field>
          <Field dark={dark} label={fl('sonstiges')}><Area dark={dark} {...b('sonstiges')} rows={2} placeholder={tr('pflege.form.ph.sonstiges')} /></Field>
        </div>
      </Section>

      {/* 06 · Einsatz-Parameter — Teilmenge des Pflege-Katalogs aus BK-Sicht:
          welche Wohn-/Umfeld-Situation und Klienten-Eigenschaften die BK akzeptiert. */}
      <Section dark={dark} id="parameter" num="06" title={tr('betreuung.form.section.parameter.title')} desc={tr('betreuung.form.section.parameter.desc')}>
        {/* Batch-12: SGB5 — BK übernimmt Behandlungspflege (SGB V) */}
        <div style={{ marginBottom: 18 }}>
          <ToggleRow
            dark={dark}
            {...bBool('sgb5_bk')}
            label={bl('sgb5_bk')}
            desc={bl('sgb5_bk_desc')}
          />
        </div>
        {/* Wohnsituation/Wohnort als Mehrfachauswahl: das Matching prüft,
            ob die Situation des PB unter den akzeptierten Werten ist. */}
        <Field dark={dark} label={fl('wohnsituation')} hint={tr('pflege.form.hint.multi')}>
          <CheckGroup dark={dark} {...bMulti('wohnsituation')} cols={2} options={opt('wohnsituation')} />
        </Field>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('wohnort')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('wohnort')} cols={3} options={opt('wohnort')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('haustiere')}><Select dark={dark} {...b('haustiere')} placeholder="—" options={opt('haustiere')} /></Field>
          </Grid>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('haustiere_welche')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('haustiere_welche')} cols={3} options={opt('haustiere_welche')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('charaktereigenschaften')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('charaktereigenschaften')} cols={2} options={opt('charaktereigenschaften')} />
          </Field>
        </div>
        {/* Pflege-Parameter (wie CRM 1.0: Parameter-Block wird auch für BKs gerendert) */}
        <GroupLabel dark={dark}>{tr('pflege.form.section.pflege.title')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('pflegegrad')}><Select dark={dark} {...b('pflegegrad')} placeholder="—" options={opt('pflegegrad')} /></Field>
        </Grid>
        {/* Batch-12: Beweglichkeit als Mehrfachauswahl — die BK kann mehrere
            Mobilitätsgrade der Klienten abdecken. */}
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('beweglichkeit')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('beweglichkeit')} cols={3} options={opt('beweglichkeit')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('einschraenkungen')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('einschraenkungen')} cols={3} options={opt('einschraenkungen')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('sinneswahrnehmung')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('sinneswahrnehmung')} cols={2} options={opt('sinneswahrnehmung')} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('geisteszustand')}><Select dark={dark} {...b('geisteszustand')} placeholder="—" options={opt('geisteszustand')} /></Field>
            <Field dark={dark} label={fl('schlafverhalten')}><Select dark={dark} {...b('schlafverhalten')} placeholder="—" options={opt('schlafverhalten')} /></Field>
          </Grid>
        </div>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.support')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('grundpflege')}><Select dark={dark} {...b('grundpflege')} placeholder="—" options={opt('grundpflege')} /></Field>
          <Field dark={dark} label={fl('hygiene')}><Select dark={dark} {...b('hygiene')} placeholder="—" options={opt('hygiene')} /></Field>
          <Field dark={dark} label={fl('ankleiden')}><Select dark={dark} {...b('ankleiden')} placeholder="—" options={opt('ankleiden')} /></Field>
          <Field dark={dark} label={fl('essen')}><Select dark={dark} {...b('essen')} placeholder="—" options={opt('essen')} /></Field>
          <Field dark={dark} label={fl('urinkontrolle')}><Select dark={dark} {...b('urinkontrolle')} placeholder="—" options={opt('urinkontrolle')} /></Field>
          <Field dark={dark} label={fl('stuhlkontrolle')}><Select dark={dark} {...b('stuhlkontrolle')} placeholder="—" options={opt('stuhlkontrolle')} /></Field>
        </Grid>
        <GroupLabel dark={dark}>{tr('pflege.form.section.wohnen.title')}</GroupLabel>
        <div>
          <Field dark={dark} label={fl('wohnzustand')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('wohnzustand')} cols={2} options={opt('wohnzustand')} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('einkaufsmoeglichkeiten')}><Select dark={dark} {...b('einkaufsmoeglichkeiten')} placeholder="—" options={opt('einkaufsmoeglichkeiten')} /></Field>
          </Grid>
        </div>
      </Section>

      {/* 10 · Status */}
      <Section dark={dark} id="status" num="10" title={tr('pflege.form.section.status.title')} desc={tr('betreuung.form.status_desc')}>
        <Grid cols={3}>
          <Field dark={dark} label={fl('badge')}><Select dark={dark} {...b('badge')} options={HARDCODED.badge} /></Field>
          <Field dark={dark} label={fl('impfstatus')}><Select dark={dark} {...b('impfstatus')} placeholder="—" options={HARDCODED.impfstatus} /></Field>
          <Field dark={dark} label={fl('rating')} hint={tr('pflege.form.hint.readonly')}>
            <div style={{ paddingTop: 8 }}><Rating dark={dark} value={f.rating ?? 0} /></div>
          </Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={tr('pflege.edit.header.status')} hint={tr('betreuung.form.status_hint')}><Select dark={dark} {...b('status')} placeholder="—" options={bkStatusOptions(tr, f.status)} /></Field>
          </Grid>
        </div>
        <GroupLabel dark={dark}>{tr('betreuung.form.glabel_pause')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('pause_von')}><DateField dark={dark} {...b('pause_von')} ariaLabel={fl('pause_von')} /></Field>
          <Field dark={dark} label={fl('pause_bis')}><DateField dark={dark} {...b('pause_bis')} ariaLabel={fl('pause_bis')} /></Field>
        </Grid>
      </Section>

      {/* 11 · Bank */}
      <Section dark={dark} id="bank" num="11" title={tr('pflege.form.section.bank.title')} desc={tr('pflege.form.section.bank.desc')}>
        <Grid cols={2}>
          <Field dark={dark} label={fl('kontoinhaber')}><Text dark={dark} {...b('kontoinhaber')} placeholder={tr('pflege.form.ph.kontoinhaber')} /></Field>
          <Field dark={dark} label={fl('bankname')}><Text dark={dark} {...b('bankname')} placeholder={tr('pflege.form.ph.bankname')} /></Field>
          <Field dark={dark} label={fl('iban')} span={2}><Text dark={dark} {...b('iban')} placeholder="DE00 0000 0000 0000 0000 00" /></Field>
        </Grid>
      </Section>

      {/* 12 · Sonstiges */}
      <Section dark={dark} id="sonstiges" num="12" title={tr('pflege.form.section.sonstiges.title')} desc={tr('betreuung.form.section.sonstiges.desc')}>
        <Field dark={dark} label={fl('description')}><Area dark={dark} {...b('description')} rows={3} placeholder={tr('pflege.form.ph.description')} /></Field>
      </Section>

      {/* 13 · Vertrag & Preise — eigener Endpunkt (POST price), nicht Teil des Form-Saves */}
      <BkVertragSection dark={dark} user={user ?? null} />

      {/* 14 · Ausgleichsbetrag — eigener Endpunkt (POST bonus) */}
      <BkBonusSection dark={dark} user={user ?? null} />

      {/* 15 · Ansprechpartner — CRUD über v1Contacts */}
      <BkContactsSection dark={dark} userId={user?.id ?? null} />

    </div>
  );
}

/* ─── 13 · Vertrag & Preise (BK) ────────────────────────────────────────── */
function BkVertragSection({ dark, user }: { dark: boolean; user: V1User | null }) {
  const { t: tr } = useTranslation();
  const optLabel = useOptionLabel();
  /** Vertrags-Slug (avp/avd/…) → übersetztes Label; Unbekanntes bleibt roh. */
  const vertragLabel = (v: unknown) => {
    if (v == null || v === '') return '—';
    const s = String(v);
    return optLabel(BK_HARDCODED.vertrag.find(o => o.value === s)?.label ?? s);
  };
  // Eigener Form-State — legt beim Absenden eine NEUE prices_bk-Zeile an
  // (Vertragsanpassung wie in CRM 1.0), statt bestehende zu editieren.
  const [gueltigAb, setGueltigAb] = useState<string>(() => new Date().toISOString().slice(0, 7));
  const [vertrag, setVertrag] = useState<string>('');
  const [betrag, setBetrag] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  // Lokale Kopie der Historie, damit ein neuer Eintrag sofort sichtbar wird.
  const [prices, setPrices] = useState<Array<Record<string, unknown>>>(
    () => (Array.isArray(user?.prices) ? user.prices : []),
  );
  useEffect(() => { setPrices(Array.isArray(user?.prices) ? user.prices : []); }, [user]);

  const submit = async () => {
    if (!user?.id || busy) return;
    setError(null); setOk(null);
    if (!gueltigAb || !vertrag || betrag === '') {
      setError(tr('betreuung.form.vertrag.missing'));
      return;
    }
    setBusy(true);
    try {
      await v1Users.createPrice(user.id, {
        gueltig_ab: gueltigAb,
        vertrag,
        auszahlungsbetrag: betrag,
      });
      // Optimistisch in die Historie übernehmen (Backend liefert sortierte Liste,
      // aber die volle User-Struktur kommt erst beim nächsten Reload).
      setPrices(prev => [{ gueltig_ab: gueltigAb, vertrag, auszahlungsbetrag: betrag }, ...prev]);
      setOk(tr('betreuung.form.vertrag.saved'));
      setBetrag('');
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section dark={dark} id="vertrag" num="13" title={tr('betreuung.form.section.vertrag.title')} desc={tr('betreuung.form.section.vertrag.desc')}>
      <Grid cols={3}>
        <Field dark={dark} label={tr('betreuung.form.field.gueltig_ab')}>
          <Text dark={dark} value={gueltigAb} onChange={setGueltigAb} type="month" />
        </Field>
        <Field dark={dark} label={tr('betreuung.form.field.vertrag')}>
          <Select dark={dark} value={vertrag} onChange={setVertrag} placeholder="—" options={BK_HARDCODED.vertrag} />
        </Field>
        <Field dark={dark} label={tr('betreuung.form.field.auszahlungsbetrag')}>
          <Text dark={dark} value={betrag} onChange={setBetrag} type="number" prefix="€" suffix={tr('betreuung.form.field.per_month')} placeholder="1500" />
        </Field>
      </Grid>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button variant="secondary" size="sm" dark={dark} onClick={submit} disabled={!user?.id || busy}>
          {busy ? tr('pflege.edit.saving') : tr('betreuung.form.vertrag.add')}
        </Button>
        <ActionStatus dark={dark} error={error} ok={ok} />
      </div>
      {prices.length > 0 && (
        <>
          <GroupLabel dark={dark}>{tr('betreuung.form.vertrag.history')}</GroupLabel>
          <HistoryTable
            dark={dark}
            columns={[tr('betreuung.form.field.gueltig_ab'), tr('betreuung.form.field.vertrag'), tr('betreuung.form.field.auszahlungsbetrag')]}
            rows={prices.map(p => [asMonth(p.gueltig_ab), vertragLabel(p.vertrag), asEuro(p.auszahlungsbetrag)])}
          />
        </>
      )}
    </Section>
  );
}

/* ─── 14 · Ausgleichsbetrag (Bonus) ─────────────────────────────────────── */
function BkBonusSection({ dark, user }: { dark: boolean; user: V1User | null }) {
  const { t: tr } = useTranslation();
  const [gueltigFuer, setGueltigFuer] = useState<string>(() => new Date().toISOString().slice(0, 7));
  const [bonus, setBonus] = useState<string>('');
  const [kommentar, setKommentar] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [boni, setBoni] = useState<Array<Record<string, unknown>>>(
    () => (Array.isArray(user?.boni) ? user.boni as Array<Record<string, unknown>> : []),
  );
  useEffect(() => { setBoni(Array.isArray(user?.boni) ? user.boni as Array<Record<string, unknown>> : []); }, [user]);

  const submit = async () => {
    if (!user?.id || busy) return;
    setError(null); setOk(null);
    if (!gueltigFuer || bonus === '') {
      setError(tr('betreuung.form.bonus.missing'));
      return;
    }
    setBusy(true);
    try {
      const k = kommentar.trim() || null;
      // v2 speichert den Kommentar mit (v1 createBonus verwarf ihn) und
      // liefert die vollständige Liste des Users zurück.
      const res = await v2PriceBonus.create(user.id, { gueltig_fuer: gueltigFuer, bonus, kommentar: k });
      setBoni(prev => (Array.isArray(res?.boni) && res.boni.length > 0
        ? res.boni as Array<Record<string, unknown>>
        : [{ gueltig_fuer: gueltigFuer, bonus, kommentar: k }, ...prev]));
      setOk(tr('betreuung.form.bonus.saved'));
      setBonus('');
      setKommentar('');
    } catch (e) {
      setError(priceBonusErrorMessage(e, tr('pflege.edit.save_failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section dark={dark} id="bonus" num="14" title={tr('betreuung.form.section.bonus.title')} desc={tr('betreuung.form.section.bonus.desc')}>
      <Grid cols={2}>
        <Field dark={dark} label={tr('betreuung.form.field.gueltig_fuer')}>
          <Text dark={dark} value={gueltigFuer} onChange={setGueltigFuer} type="month" />
        </Field>
        <Field dark={dark} label={tr('betreuung.form.field.bonus')}>
          <Text dark={dark} value={bonus} onChange={setBonus} type="number" prefix="€" placeholder="100" />
        </Field>
        {/* Kommentar — erscheint im Excel-Export in einer eigenen Spalte */}
        <Field dark={dark} label={tr('betreuung.form.field.bonus_kommentar')} span={2}>
          <Text dark={dark} value={kommentar} onChange={setKommentar} />
        </Field>
      </Grid>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button variant="secondary" size="sm" dark={dark} onClick={submit} disabled={!user?.id || busy}>
          {busy ? tr('pflege.edit.saving') : tr('betreuung.form.bonus.add')}
        </Button>
        <ActionStatus dark={dark} error={error} ok={ok} />
      </div>
      {boni.length > 0 && (
        <>
          <GroupLabel dark={dark}>{tr('betreuung.form.bonus.history')}</GroupLabel>
          <HistoryTable
            dark={dark}
            columns={[tr('betreuung.form.field.gueltig_fuer'), tr('betreuung.form.field.bonus'), tr('betreuung.form.field.bonus_kommentar')]}
            rows={boni.map(b2 => [asMonth(b2.gueltig_fuer), asEuro(b2.bonus), String(b2.kommentar ?? '') || '—'])}
          />
        </>
      )}
    </Section>
  );
}

/* ─── 15 · Ansprechpartner ──────────────────────────────────────────────── */

/** Editierbare Kontakt-Zeile im lokalen State (Teilmenge von V1Contact). */
interface BkContactForm {
  id: number;
  kategorie: string;           // erste Kategorie-Id als String ('' = keine)
  vorname: string;
  nachname: string;
  telefon: string;
  mobile: string;
  email: string;
}

/** V1Contact.kategorie (number|string|Array|null) → erste Id als String. */
function firstKategorie(v: V1Contact['kategorie']): string {
  if (Array.isArray(v)) return v.length ? String(v[0]) : '';
  if (typeof v === 'string') {
    // JSON-Array-Serialisierung ("[3]") defensiv parsen
    if (v.startsWith('[')) {
      try { const p = JSON.parse(v); return Array.isArray(p) && p.length ? String(p[0]) : ''; } catch { return ''; }
    }
    return v;
  }
  return v == null ? '' : String(v);
}

function BkContactsSection({ dark, userId }: { dark: boolean; userId: number | null }) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const [types, setTypes] = useState<V1ContactType[]>([]);
  const [contacts, setContacts] = useState<BkContactForm[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Kontakt-Typen + Kontakte des Users laden (Muster: PflegeClientDetail-Drawer)
  useEffect(() => {
    let alive = true;
    v1Contacts.types().then(rows => { if (alive) setTypes(rows); }).catch(() => { /* Labels optional */ });
    if (userId != null) {
      v1Contacts.listForUser(userId, { perPage: 50 })
        .then(page => {
          if (!alive) return;
          setContacts(page.data.map(c => ({
            id: c.id,
            kategorie: firstKategorie(c.kategorie),
            vorname: c.vorname ?? '',
            nachname: c.nachname ?? '',
            telefon: c.telefon ?? '',
            mobile: c.mobile ?? '',
            email: c.email ?? '',
          })));
        })
        .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('betreuung.form.kontakte.load_failed')); });
    }
    return () => { alive = false; };
  }, [userId]);

  const setField = (id: number, key: keyof BkContactForm, val: string) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, [key]: val } : c));
    setOk(null);
  };

  /** Payload einer Kontakt-Zeile für create/update. */
  const payloadOf = (c: BkContactForm): Record<string, unknown> => ({
    kategorie: c.kategorie ? [Number(c.kategorie)] : [],
    vorname: c.vorname,
    nachname: c.nachname,
    telefon: c.telefon,
    mobile: c.mobile,
    email: c.email,
  });

  const saveAll = async () => {
    if (busy || contacts.length === 0) return;
    setBusy(true); setError(null); setOk(null);
    try {
      await Promise.all(contacts.map(c => v1Contacts.update(c.id, payloadOf(c))));
      setOk(tr('betreuung.form.kontakte.saved'));
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setBusy(false);
    }
  };

  const addContact = async () => {
    if (busy || userId == null) return;
    setBusy(true); setError(null); setOk(null);
    try {
      // Leeren Kontakt anlegen — Felder werden danach inline editiert.
      const created = await v1Contacts.create({ user_id: userId, kategorie: [], vorname: '', nachname: '' });
      setContacts(prev => [...prev, {
        id: created.id,
        kategorie: firstKategorie(created.kategorie),
        vorname: created.vorname ?? '',
        nachname: created.nachname ?? '',
        telefon: created.telefon ?? '',
        mobile: created.mobile ?? '',
        email: created.email ?? '',
      }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setBusy(false);
    }
  };

  const removeContact = async (id: number) => {
    if (busy) return;
    setBusy(true); setError(null); setOk(null);
    try {
      await v1Contacts.remove(id);
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      // Backend verweigert z.B. das Löschen des Account-Login-Kontakts.
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setBusy(false);
    }
  };

  const typeOptions = types.map(ty => ({ value: String(ty.id), label: ty.name }));

  return (
    <Section dark={dark} id="ansprechpartner" num="15" title={tr('betreuung.form.section.ansprechpartner.title')} desc={tr('betreuung.form.section.ansprechpartner.desc')}>
      {contacts.length === 0 && (
        <div style={{ fontSize: 13, color: t.muted, fontFamily: 'Outfit,sans-serif' }}>
          {tr('betreuung.form.kontakte.empty')}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {contacts.map((c, i) => (
          <div key={c.id} style={{ border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {tr('betreuung.form.kontakte.row', { n: i + 1 })}
              </span>
              <button
                type="button"
                onClick={() => removeContact(c.id)}
                disabled={busy}
                style={{
                  background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8,
                  padding: '4px 10px', cursor: 'pointer', color: '#b42318',
                  fontSize: 12, fontFamily: 'Outfit,sans-serif',
                }}>
                {tr('betreuung.form.kontakte.delete')}
              </button>
            </div>
            <Grid cols={3}>
              <Field dark={dark} label={tr('betreuung.form.kontakte.kategorie')}>
                <Select dark={dark} value={c.kategorie} onChange={v => setField(c.id, 'kategorie', v)} placeholder="—" options={typeOptions} />
              </Field>
              <Field dark={dark} label={tr('pflege.form.field.vorname')}>
                <Text dark={dark} value={c.vorname} onChange={v => setField(c.id, 'vorname', v)} />
              </Field>
              <Field dark={dark} label={tr('pflege.form.field.nachname')}>
                <Text dark={dark} value={c.nachname} onChange={v => setField(c.id, 'nachname', v)} />
              </Field>
              <Field dark={dark} label={tr('pflege.form.field.telefon')}>
                <Text dark={dark} value={c.telefon} onChange={v => setField(c.id, 'telefon', v)} type="tel" />
              </Field>
              <Field dark={dark} label={tr('pflege.form.field.mobile')}>
                <Text dark={dark} value={c.mobile} onChange={v => setField(c.id, 'mobile', v)} type="tel" />
              </Field>
              <Field dark={dark} label={tr('pflege.form.field.email')}>
                <Text dark={dark} value={c.email} onChange={v => setField(c.id, 'email', v)} type="email" />
              </Field>
            </Grid>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button variant="secondary" size="sm" dark={dark} onClick={addContact} disabled={busy || userId == null}>
          {tr('betreuung.form.kontakte.add')}
        </Button>
        {contacts.length > 0 && (
          <Button variant="secondary" size="sm" dark={dark} onClick={saveAll} disabled={busy}>
            {busy ? tr('pflege.edit.saving') : tr('betreuung.form.kontakte.save')}
          </Button>
        )}
        <ActionStatus dark={dark} error={error} ok={ok} />
      </div>
    </Section>
  );
}
